import { useLoading } from "~/contexts/LoadingContext";

export function Skeleton({
  width = "100%",
  height = "1rem",
  className = "",
  style = {},
}: {
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        backgroundColor: "#f0f2f5",
        borderRadius: "2px",
        display: "inline-block",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: "linear-gradient(90deg, #f0f2f5 25%, #e6e8eb 50%, #f0f2f5 75%)",
          backgroundSize: "200% 100%",
          animation: "skeleton-loading 1.5s infinite",
        }}
      />
    </div>
  );
}

export function ContentLoadingOverlay({ 
  isLoading,
  collapsed 
}: { 
  isLoading: boolean; 
  collapsed: boolean 
}) {
  const { isLoading: contextIsLoading } = useLoading();
  const showLoading = isLoading || contextIsLoading;   

  if (!showLoading) return null;
  
  return (
    <div
      style={{
        position: "fixed",
        top: 80,
        left: collapsed ? 96 : 216,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        backdropFilter: "blur(8px)",
        zIndex: 1001,
        padding: "32px 24px",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: collapsed ? "calc(100% - 112px)" : "calc(100% - 232px)",
      }}
    >
      {/* 加载动画内容 */}
      <div
        style={{
          width: "100%",
          margin: "0 auto",
          animation: "fadeIn 0.3s ease-out",
        }}
      >
        {/* 标题区域 */}
        <div
          style={{
            marginBottom: "32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Skeleton
            width="240px"
            height="40px"
            style={{
              borderRadius: "8px",
              marginBottom: "8px",
            }}
          />
          <Skeleton
            width="120px"
            height="40px"
            style={{
              borderRadius: "8px",
              marginLeft: "auto",
            }}
          />
        </div>

        {/* 搜索和筛选区域 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <Skeleton
            width="100%"
            height="32px"
            style={{
              borderRadius: "8px",
            }}
          />
          <Skeleton
            width="100%"
            height="32px"
            style={{
              borderRadius: "8px",
            }}
          />
          <Skeleton
            width="100%"
            height="32px"
            style={{
              borderRadius: "8px",
            }}
          />
        </div>

        {/* 表格区域 */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            marginBottom: "24px",
          }}
        >
          {/* 表头 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "16px",
              padding: "16px 24px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                width="80%"
                height="20px"
                style={{
                  borderRadius: "4px",
                }}
              />
            ))}
          </div>

          {/* 表格行 */}
          {[...Array(6)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "16px",
                padding: "20px 24px",
                borderBottom: "1px solid #f5f5f5",
                backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
              }}
            >
              {[...Array(5)].map((_, colIndex) => (
                <Skeleton
                  key={colIndex}
                  width={colIndex === 0 ? "60%" : "90%"}
                  height="16px"
                  style={{
                    borderRadius: "4px",
                    opacity: 0.9 - rowIndex * 0.1,
                  }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* 分页 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 0",
          }}
        >
          <Skeleton
            width="120px"
            height="32px"
            style={{
              borderRadius: "6px",
            }}
          />
          <div style={{ display: "flex", gap: "8px" }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                width="40px"
                height="40px"
                style={{
                  borderRadius: "6px",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}